import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';
import {
  americanToDecimal,
  decimalToAmerican,
  americanToImplied,
  impliedToAmerican,
  fractionalToDecimal,
  decimalToFractional,
  toWin,
  payout,
} from '../utils/oddsCalculations';

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${(props) => props.theme.secondaryText};
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const Card = styled.div`
  background: ${(props) => props.theme.cardBg};
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: ${(props) => props.theme.shadow};
  border: 1px solid ${(props) => props.theme.border};
  margin-bottom: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryText};
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  font-size: 1rem;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.primaryText};
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
  &::placeholder {
    color: ${(props) => props.theme.mutedText};
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  font-size: 1rem;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.primaryText};
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
`;

const OutputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const OutputBox = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.35rem;
  background: ${(props) => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border: 1px solid ${(props) => props.theme.border};
`;

const OutputValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
`;

const ResetBtn = styled.button`
  display: block;
  margin: 1.25rem auto 0;
  padding: 0.5rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.secondaryBg};
  color: ${(props) => props.theme.secondaryText};
  cursor: pointer;
  &:hover {
    background: ${(props) => props.theme.tertiaryBg};
    color: ${(props) => props.theme.primaryText};
  }
`;

const ParlayLink = styled(Link)`
  font-size: 0.9rem;
  color: ${(props) => props.theme.primary};
  margin-left: 0.5rem;
  &:hover {
    text-decoration: underline;
  }
`;

const OddsCalculatorPage = () => {
  const { theme } = useTheme();
  usePageTitle('Betting Odds Calculator – Implied Probability & Payouts');
  const [betAmount, setBetAmount] = useState('10');
  const [americanInput, setAmericanInput] = useState('-150');
  const [betType, setBetType] = useState('SINGLE BET');

  const stake = parseFloat(betAmount) || 0;
  const americanNum = (() => {
    const s = americanInput.trim();
    if (!s) return null;
    const n = parseInt(s, 10);
    if (Number.isNaN(n)) return null;
    return n;
  })();

  const decimal = americanNum != null ? americanToDecimal(americanNum) : null;
  const fractional = decimal != null && decimal > 1 ? decimalToFractional(decimal) : '';
  const impliedPct = americanNum != null ? americanToImplied(americanNum) * 100 : null;
  const toWinAmount = americanNum != null && stake > 0 ? toWin(americanNum, stake) : 0;
  const payoutAmount = americanNum != null && stake > 0 ? payout(americanNum, stake) : 0;

  const setAmericanFromDecimal = useCallback((decStr) => {
    if (decStr.trim() === '') {
      setAmericanInput('');
      return;
    }
    const d = parseFloat(decStr);
    if (!Number.isNaN(d) && d > 1) {
      const a = decimalToAmerican(d);
      if (a != null) setAmericanInput(String(a));
    }
  }, []);

  const setAmericanFromFractional = useCallback((fracStr) => {
    if (fracStr.trim() === '') {
      setAmericanInput('');
      return;
    }
    const d = fractionalToDecimal(fracStr);
    if (d != null && d > 1) {
      const a = decimalToAmerican(d);
      if (a != null) setAmericanInput(String(a));
    }
  }, []);

  const setAmericanFromImplied = useCallback((pctStr) => {
    if (pctStr.trim() === '') {
      setAmericanInput('');
      return;
    }
    const p = parseFloat(pctStr);
    if (!Number.isNaN(p)) {
      const a = impliedToAmerican(p >= 1 && p <= 100 ? p / 100 : p);
      if (a != null) setAmericanInput(String(a));
    }
  }, []);

  const handleReset = () => {
    setBetAmount('10');
    setAmericanInput('-150');
    setBetType('SINGLE BET');
  };

  return (
    <Container>
      <PageMeta description="Convert American, Decimal, Fractional odds and implied probability, and calculate payouts for single bets and parlays using the Slick Picks betting odds calculator." />
      <Title theme={theme}>Betting Odds Calculator</Title>
      <Subtitle theme={theme}>
        Input your stake and odds in American, Decimal, or Fractional formats to quickly calculate payouts and
        implied probability. Use this alongside today&apos;s MLB player prop edges from the{' '}
        <a href="/mlb/total-bases">Total Bases model</a> or{' '}
        <a href="/game-picks">Game Picks</a> page to sanity-check prices across books.
      </Subtitle>

      <Card theme={theme}>
        <Row>
          <Field>
            <Label theme={theme}>Bet Amount</Label>
            <Input
              theme={theme}
              type="number"
              min="0"
              step="0.01"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field>
            <Label theme={theme}>Bet Type</Label>
            <Select theme={theme} value={betType} onChange={(e) => setBetType(e.target.value)}>
              <option value="SINGLE BET">Single Bet</option>
              <option value="PARLAY">Parlay</option>
            </Select>
            {betType === 'PARLAY' && (
              <ParlayLink theme={theme} to="/resources/parlay-calculator">
                Use Parlay Calculator →
              </ParlayLink>
            )}
          </Field>
        </Row>

        <Row>
          <Field>
            <Label theme={theme}>American Odds</Label>
            <Input
              theme={theme}
              type="text"
              value={americanInput}
              onChange={(e) => setAmericanInput(e.target.value)}
              placeholder="e.g. -150 or +110"
            />
          </Field>
          <Field>
            <Label theme={theme}>Decimal Odds</Label>
            <Input
              theme={theme}
              type="number"
              min="1.01"
              step="0.01"
              value={decimal != null ? decimal.toFixed(4).replace(/\.?0+$/, '') : ''}
              onChange={(e) => setAmericanFromDecimal(e.target.value)}
              placeholder="e.g. 1.67"
            />
          </Field>
        </Row>

        <Row>
          <Field>
            <Label theme={theme}>Fractional Odds</Label>
            <Input
              theme={theme}
              type="text"
              value={fractional}
              onChange={(e) => setAmericanFromFractional(e.target.value)}
              placeholder="e.g. 2/3"
            />
          </Field>
          <Field>
            <Label theme={theme}>Implied Probability (%)</Label>
            <Input
              theme={theme}
              type="number"
              min="0.01"
              max="99.99"
              step="0.01"
              value={impliedPct != null ? impliedPct.toFixed(2) : ''}
              onChange={(e) => setAmericanFromImplied(e.target.value)}
              placeholder="e.g. 60"
            />
          </Field>
        </Row>

        <OutputRow theme={theme}>
          <OutputBox theme={theme}>
            <Label theme={theme}>To Win</Label>
            <OutputValue theme={theme}>
              $ {toWinAmount.toFixed(2)}
            </OutputValue>
          </OutputBox>
          <OutputBox theme={theme}>
            <Label theme={theme}>Payout</Label>
            <OutputValue theme={theme}>
              $ {payoutAmount.toFixed(2)}
            </OutputValue>
          </OutputBox>
        </OutputRow>

        <ResetBtn theme={theme} type="button" onClick={handleReset}>
          Reset
        </ResetBtn>
      </Card>
    </Container>
  );
};

export default OddsCalculatorPage;
